import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Play, FileSpreadsheet, ClipboardList, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StockCountList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/stock-count/sessions`);
        if (alive) setSessions(res.data?.sessions || []);
      } catch {
        toast.error("Couldn't load sessions");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    // Poll every 5s so a shared session's progress updates on the list too.
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const fmtDate = (v) => v ? new Date(v).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const active = sessions.filter((s) => s.status === "active");
  const closed = sessions.filter((s) => s.status !== "active");

  const downloadXlsx = async (code) => {
    try {
      const res = await axios.get(`${API}/stock-count/sessions/${code}/xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `stock-count-${code}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="h-6 w-6" /> Stock Count</h1>
          <p className="text-sm text-slate-500">Scan-driven physical inventory counts. Pair a PC + phones on the same session code.</p>
        </div>
        <Button onClick={() => navigate("/stock-count/new")} data-testid="new-session-btn">
          <Plus className="h-4 w-4 mr-1" /> New session
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Active sessions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-slate-500">Loading…</p>
            : active.length === 0 ? <p className="text-sm text-slate-500">No active sessions. Start one to begin counting.</p>
            : (
              <div className="space-y-2">
                {active.map((s) => (
                  <div key={s.code} className="border rounded p-3 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold flex items-center gap-2">
                        {s.name}
                        <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{s.code}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Scope: {s.scope?.type}{s.scope?.value ? ` = ${s.scope.value}` : ""} · {s.blind ? "Blind" : "Open"} · Started {fmtDate(s.created_at)}
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800"><Clock className="h-3 w-3 mr-1" /> In progress</Badge>
                    <Button size="sm" onClick={() => navigate(`/stock-count/session/${s.code}`)} data-testid={`open-${s.code}`}>
                      <Play className="h-4 w-4 mr-1" /> Open
                    </Button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-slate-500">Loading…</p>
            : closed.length === 0 ? <p className="text-sm text-slate-500">No closed sessions yet.</p>
            : (
              <div className="space-y-2">
                {closed.map((s) => (
                  <div key={s.code} className="border rounded p-3 flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-semibold flex items-center gap-2">
                        {s.name}
                        <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{s.code}</span>
                        <Badge variant="outline" className={s.status === "closed" ? "border-blue-300 text-blue-700" : "border-slate-300 text-slate-600"}>
                          {s.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {s.scope?.type}{s.scope?.value ? ` = ${s.scope.value}` : ""} · Closed {fmtDate(s.closed_at)}
                        {s.variance_summary && ` · ${s.variance_summary.applied} adjustments applied · net Δ ${s.variance_summary.net_units_delta}`}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/stock-count/session/${s.code}`)}>
                      View
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadXlsx(s.code)}>
                      <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                    </Button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
